import { useId } from "react";
import type { GameId } from "@hunch/core";

const ACCENTS: Record<GameId, { bg: string; fg: string }> = {
  timeslip: { bg: "#fff3d6", fg: "#d99400" },
  crowflight: { bg: "#dce6ff", fg: "#2e6bff" },
  huesswork: { bg: "#ede0fb", fg: "#9b5de5" },
};

/**
 * Simple themed tile icon per game, see docs/style.md. A light version of
 * the richer illustrations described in spec §12, which land alongside
 * each game's own milestone.
 */
export function GameIcon({
  gameId,
  className,
}: {
  gameId: GameId;
  className?: string | undefined;
}) {
  const { bg, fg } = ACCENTS[gameId];
  const gradientId = useId();
  return (
    <svg viewBox="0 0 96 96" className={className} role="presentation" aria-hidden>
      {gameId !== "timeslip" && <circle cx="48" cy="48" r="46" fill={bg} />}
      {gameId === "timeslip" && (
        <g>
          <rect x="4" y="4" width="88" height="88" rx="22" fill={bg} />
          <rect x="10" y="28" width="12" height="36" rx="3" fill={fg} />
          <rect x="26" y="28" width="12" height="36" rx="3" fill={fg} />
          <rect x="42" y="28" width="12" height="36" rx="3" fill={fg} />
          <rect x="58" y="28" width="12" height="36" rx="3" fill={fg} />
          <rect x="74" y="28" width="12" height="36" rx="3" fill={fg} />
          <circle cx="16" cy="76" r="2" fill={fg} />
          <circle cx="32" cy="76" r="2.5" fill={fg} />
          <circle cx="48" cy="76" r="3" fill={fg} />
          <circle cx="64" cy="76" r="3.5" fill={fg} />
          <circle cx="80" cy="76" r="4" fill={fg} />
        </g>
      )}
      {gameId === "crowflight" && (
        <g>
          <circle cx="48" cy="48" r="27" fill="none" stroke={fg} strokeWidth="3" />
          <path d="M 21 48 H 75" stroke={fg} strokeWidth="2" strokeLinecap="round" fill="none" />
          <ellipse cx="48" cy="48" rx="10" ry="27" stroke={fg} strokeWidth="2" fill="none" />
          <ellipse
            cx="48"
            cy="50"
            rx="36"
            ry="17"
            transform="rotate(-18 48 50)"
            stroke={fg}
            strokeWidth="1.5"
            strokeDasharray="3 5"
            fill="none"
            opacity="0.5"
          />
          <g transform="translate(78 26) rotate(-20)">
            <circle cx="-2" cy="-6" r="3.6" fill="none" stroke={fg} strokeWidth="1.4" opacity="0.7" />
            <circle cx="2.5" cy="-6.5" r="3.1" fill="none" stroke={fg} strokeWidth="1.4" opacity="0.7" />
            <ellipse cx="0" cy="0" rx="7.5" ry="4.5" fill={fg} />
            <path d="M -2.5 -4 L -2.5 4" stroke={bg} strokeWidth="1.8" strokeLinecap="round" />
            <path d="M 1.5 -4.2 L 1.5 4.2" stroke={bg} strokeWidth="1.8" strokeLinecap="round" />
            <circle cx="7.3" cy="0" r="2.6" fill={fg} />
            <path d="M 8.8 -1.5 L 11 -4" stroke={fg} strokeWidth="1" strokeLinecap="round" fill="none" />
            <path d="M 9.3 0.5 L 11.5 1.5" stroke={fg} strokeWidth="1" strokeLinecap="round" fill="none" />
          </g>
        </g>
      )}
      {gameId === "huesswork" && (
        <g>
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={fg} />
              <stop offset="100%" stopColor={bg} />
            </linearGradient>
          </defs>
          <rect x="16" y="34" width="64" height="28" rx="8" fill={`url(#${gradientId})`} />
          <path d="M 48 28 V 68" stroke="#ffffff" strokeWidth="5" strokeLinecap="round" />
        </g>
      )}
    </svg>
  );
}
